import type { CaseStudyId } from "~/content/cases"

export type CaseStudyStory = {
  heading: string
  body: string
  image?: string
  cover?: CaseStudyId
  steps?: readonly string[]
}

export type CaseStudyPresentation = {
  headline: string
  summary: string
  facts: readonly { label: string; value: string }[]
  brief: string
  briefBody: string
  rows: readonly CaseStudyStory[]
  outcome: { label: string; heading: string; body: string; quote: boolean }
}

export const caseStudyPresentations: Record<CaseStudyId, CaseStudyPresentation> = {
  "brightlines": {
    headline: "Find the exposure.\nHelp people\ntake it back.",
    summary:
      "An operations platform for teams finding and removing exposed personal information online.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Product + design + development + AI",
      },
      {
        label: "PLATFORMS",
        value: "Web",
      },
      {
        label: "CONTEXT",
        value: "Privacy operations",
      },
    ],
    brief: "Give analysts one system to work in.",
    briefBody:
      "Brightlines protects people at risk of doxxing. As demand grew, its analysts needed a system to replace spreadsheets and prototype tools, connecting client intake, searches, review, and reporting.",
    rows: [
      {
        heading: "Connect intake to investigation.",
        body: "We started with Sprint Zero in June 2024 and launched V1 that fall. Searches across breach, username, and image sources brought findings into a shared workflow for analysts to review.",
        steps: ["Client intake", "Search sources", "Analyst review"],
      },
      {
        heading: "Use AI where it helps the team.",
        body: "Verdict AI combines rules and an LLM to categorize results and process data-broker findings, with analysts kept in the loop. The product also supports scheduled client reports and the operational work around accounts and plans.",
        steps: ["Search findings", "Rules + AI categorization", "Analyst review + client reports"],
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "A product that grows with the operation.",
      body: "The partnership continued into weekly roadmap work across reporting, staff dashboards, account management, and security improvements.",
      quote: false,
    },
  },
  "target100": {
    headline: "Healthy habits.\nOne place to\nkeep track.",
    summary: "A health program brought to life across web, mobile, and connected devices.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Sprint Zero + design + development",
      },
      {
        label: "PLATFORMS",
        value: "Web, iOS + Android",
      },
      {
        label: "CONTEXT",
        value: "Health and wellness",
      },
    ],
    brief: "Bring the program into daily life.",
    briefBody:
      "Liz Josefsberg’s Target 100 program needed a digital home. We worked with her team to turn the book’s approach into a platform where people could track food, activity, weight, and hydration without piecing together separate tools.",
    rows: [
      {
        heading: "Start with the everyday habits.",
        body: "Sprint Zero helped define the product around the people using it. We designed and built web and mobile apps with food-tracking integrations and Fitbit activity data, making the routine of checking in easier to follow.",
        image: "/images/cases/f418c.png",
      },
      {
        heading: "Let the devices do their part.",
        body: "A Fitbit, connected scale, and smart water bottle sent data into the platform. The private beta gave the team a way to learn from everyday use and refine the experience as the product took shape.",
        image: "/images/cases/01a03.png",
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "A connected view of progress.",
      body: "The program’s devices and digital tools came together in one experience, giving participants a clearer view of the habits they were working on.",
      quote: false,
    },
  },
  "leapfund": {
    headline: "See what a raise\ncould really mean.",
    summary:
      "A benefits cliff calculator that helps coaches and clients explore changes in income and support.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Product + development + AI",
      },
      {
        label: "PLATFORMS",
        value: "Web",
      },
      {
        label: "CONTEXT",
        value: "Financial coaching",
      },
    ],
    brief: "Make the cliff visible.",
    briefBody:
      "A small raise can sometimes cost a worker more in lost benefits than it adds in wages. Leap Fund needed a calculator that would help coaches and clients explore how income or household changes could affect their overall situation.",
    rows: [
      {
        heading: "Build a tool for the coaching session.",
        body: "We built Leap Fund’s benefits cliff calculator. It helps coaches explore scenarios with clients and see where changes in income could affect benefits and net income.",
        cover: "leapfund",
      },
      {
        heading: "Help people focus on what matters.",
        body: "In 2026, we redesigned results around a sidebar highlighting differences between predicted benefits and what clients report. The ongoing work also includes guidebook retrieval for a chatbot and plain-language access to program data for staff.",
        steps: [
          "Review calculator results",
          "Compare predicted and reported benefits",
          "Focus the coaching conversation",
        ],
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "A calculator that keeps evolving.",
      body: "The partnership connects product development with the needs of coaches and staff, refining the calculator and adding tools where they support the work.",
      quote: false,
    },
  },
  "heat-seek": {
    headline: "When the heat\nisn’t there,\nthe record is.",
    summary:
      "Sensors and software that help tenants and advocates document inadequate apartment heat.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Hardware + web development",
      },
      {
        label: "PLATFORMS",
        value: "Connected sensors + web",
      },
      {
        label: "CONTEXT",
        value: "Housing and tenant advocacy",
      },
    ],
    brief: "Give tenants a reliable record.",
    briefBody:
      "Heat Seek helps New Yorkers document heating problems in their apartments. Its team needed reliable sensors and reporting tools that could turn temperature readings into useful records for tenants and their advocates.",
    rows: [
      {
        heading: "Get the readings right.",
        body: "We helped shape the hardware and software roadmap, fixed reporting issues, and designed a custom circuit board with a temperature sensor and cellular communications.",
        image: "/images/cases/bb6e1.png",
      },
      {
        heading: "Turn readings into a useful log.",
        body: "The web application brings together indoor and outdoor temperature data, records periods below the applicable heat threshold, and produces graphs and heat logs. Those records support the work of tenants and their advocates.",
        image: "/images/cases/8daff.png",
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "A longer partnership behind the readings.",
      body: "The work extended across hardware, reporting, alerts, and ongoing maintenance, helping Heat Seek keep improving the tools used in its tenant advocacy work.",
      quote: false,
    },
  },
  "workweek-communities": {
    headline: "A community\nbeyond the inbox.",
    summary: "A professional network that brings conversations, feeds, and events together.",
    facts: [
      {
        label: "PRODUCT FOCUS",
        value: "Professional networking",
      },
      {
        label: "PLATFORMS",
        value: "Web",
      },
      {
        label: "CONTEXT",
        value: "Professional communities",
      },
    ],
    brief: "Give readers a place to connect.",
    briefBody:
      "WorkWeek’s Professional Network Platform was designed to create a community offering alongside its newsletters. The product brought members into a place where they could talk, share, and join events.",
    rows: [
      {
        heading: "Keep the conversation going.",
        body: "Messaging, feeds, and events formed the core of the documented product design. Together, they gave members ways to participate beyond reading a newsletter.",
        image: "/images/cases/b06bd.png",
      },
      {
        heading: "Make room for more communities.",
        body: "The design approach supported additional communities through focused branding and design work, with attention to accessibility and creator needs.",
        image: "/images/cases/0711c.png",
      },
    ],
    outcome: {
      label: "THE PRODUCT",
      heading: "A shared home for the community.",
      body: "The platform brought messaging, content, and events into a professional network.",
      quote: false,
    },
  },
  "mosaic-foods": {
    headline: "More choice\nin every\nmeal plan.",
    summary: "A flexible ordering experience for Mosaic Foods’ Buy More, Save More offering.",
    facts: [
      {
        label: "OUR ROLE",
        value: "UX design + systems integration",
      },
      {
        label: "PLATFORMS",
        value: "Web",
      },
      {
        label: "CONTEXT",
        value: "Food and commerce",
      },
    ],
    brief: "Let customers choose what fits.",
    briefBody:
      "Mosaic wanted customers to add more options instead of being locked into a fixed meal-plan level. We worked with its marketing team to shape the Buy More, Save More experience for new and returning customers.",
    rows: [
      {
        heading: "Make room for different appetites.",
        body: "We defined the experience for customers choosing and adding meal options. The flow needed to make the offering clear whether someone was ordering for the first time or returning to an existing plan.",
        image: "/images/cases/c3845.png",
      },
      {
        heading: "Work with the existing product.",
        body: "We integrated the new experience with Mosaic’s existing systems and technology. That let the team introduce the offering without rewriting the whole application.",
        image: "/images/cases/5cf65.png",
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "A more flexible way to order.",
      body: "Customers could add options to their plan, while Mosaic gained a new offering built on the systems it already used.",
      quote: false,
    },
  },
  "chill-in": {
    headline: "Chill In.\nA closer look.",
    summary: "Dashboard visuals from the Chill In project.",
    facts: [
      {
        label: "PRODUCT FOCUS",
        value: "Dashboard interface",
      },
      {
        label: "PLATFORMS",
        value: "Web dashboard",
      },
      {
        label: "PROJECT",
        value: "Chill In",
      },
    ],
    brief: "The story behind the screens.",
    briefBody: "A look at the dashboard interface from the Chill In project.",
    rows: [
      {
        heading: "The dashboard experience.",
        body: "A view of the Chill In dashboard.",
        image: "/images/cases/62f70.png",
      },
      {
        heading: "A second view of the product.",
        body: "Another view of the dashboard interface.",
        image: "/images/cases/1030d.png",
      },
    ],
    outcome: {
      label: "THE PRODUCT",
      heading: "Explore more of our work.",
      body: "See how we have helped other teams bring their products to life.",
      quote: false,
    },
  },
  "kindred": {
    headline: "Bring the network\ncloser together.",
    summary:
      "A member platform for executives to discover events, connect with peers, and grow their community.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Sprint Zero + design + development",
      },
      {
        label: "PLATFORMS",
        value: "Web",
      },
      {
        label: "CONTEXT",
        value: "Professional community",
      },
    ],
    brief: "Give the community a place to gather.",
    briefBody:
      "Kindred connects executives working on socially responsible business. Its members needed a way to find Assemblies, revisit past events, meet other members, and bring new people into the network.",
    rows: [
      {
        heading: "Make the next event easy to find.",
        body: "Beginning with Sprint Zero, we designed and developed a platform where members could discover and register for Assemblies and view past events.",
        image: "/images/cases/2a0c5.png",
      },
      {
        heading: "Help connections continue.",
        body: "The member experience also supported connecting with peers and nominating new members. Those workflows gave the network a digital home beyond the events themselves.",
        image: "/images/cases/9036b.png",
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "One place for events and connections.",
      body: "Kindred’s platform brought event discovery, registration, and member connections into a shared experience for its professional community.",
      quote: false,
    },
  },
  "quadrant": {
    headline: "See the portfolio.\nExplore what\ncomes next.",
    summary:
      "A client portal that brings portfolio performance, projections, and documents into one place.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Sprint Zero + design + development",
      },
      {
        label: "PLATFORMS",
        value: "Web",
      },
      {
        label: "CONTEXT",
        value: "Financial services",
      },
    ],
    brief: "Make the numbers easier to work with.",
    briefBody:
      "Quadrant wanted clients to understand their portfolios and explore future allocations with their advisors. Its team also needed a better way to handle documents, historical records, and repeatable tasks.",
    rows: [
      {
        heading: "Move the scenarios out of spreadsheets.",
        body: "We built a custom portal that brought Excel-based calculations and financial projections into a shared web experience. Clients and advisors could explore scenarios and discuss future allocations together.",
        image: "/images/cases/51413.png",
      },
      {
        heading: "Connect the client experience.",
        body: "The portal integrated with Quadrant’s existing Tamarac platform. Alongside the product, we refreshed the logo, brand elements, and public website to create a more consistent experience from first visit to ongoing client relationship.",
        image: "/images/cases/6c539.png",
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "A clearer place to collaborate.",
      body: "Portfolio information, projections, and operational tasks became part of a connected client experience, with a custom platform the team could continue to develop.",
      quote: false,
    },
  },
  "reportable-news": {
    headline: "Give reporters\nthe whole story.",
    summary:
      "Multimedia news releases with the information and assets journalists need to cover a story.",
    facts: [
      {
        label: "PRODUCT FOCUS",
        value: "News releases",
      },
      {
        label: "PLATFORMS",
        value: "Web",
      },
      {
        label: "CONTEXT",
        value: "Media and communications",
      },
    ],
    brief: "Put the story and its assets together.",
    briefBody:
      "Reportable helps companies build, distribute, and share news releases. The product brings the supporting media, pull quotes, and details into the release so journalists have more of what they need in one place.",
    rows: [
      {
        heading: "More than a block of text.",
        body: "Images, media, quotes, and background details give a release more context. The product presents these materials together so reporters can understand the story and find the assets that support it.",
        image: "/images/cases/ccac3.png",
      },
      {
        heading: "Make the release easier to share.",
        body: "Reportable brings building, distributing, and sharing releases into a web product. Supporting materials stay alongside the news, giving reporters a place to find the details they need.",
        image: "/images/cases/3205e.png",
      },
    ],
    outcome: {
      label: "THE PRODUCT",
      heading: "A home for the materials behind the news.",
      body: "News releases bring the story, supporting media, and background details together in one place.",
      quote: false,
    },
  },
  "chatbcc": {
    headline: "Built around\nthe group chat.",
    summary: "A mobile social product centered on group conversations.",
    facts: [
      {
        label: "PRODUCT FOCUS",
        value: "Group conversations",
      },
      {
        label: "PLATFORMS",
        value: "iOS + Android",
      },
      {
        label: "CONTEXT",
        value: "Social networking",
      },
    ],
    brief: "Start with the conversation.",
    briefBody: "ChatBCC is a mobile social networking app centered on group chats.",
    rows: [
      {
        heading: "A place for group conversations.",
        body: "Group conversations sit at the center of the ChatBCC mobile experience.",
        image: "/images/cases/f84c0.png",
      },
      {
        heading: "The mobile experience.",
        body: "ChatBCC brings its group-chat experience to iOS and Android.",
        image: "/images/cases/1550a.png",
      },
    ],
    outcome: {
      label: "THE PRODUCT",
      heading: "A product built around conversation.",
      body: "A mobile space for group chats on iOS and Android.",
      quote: false,
    },
  },
  "obe-fitness": {
    headline: "Your next workout.\nWherever you are.",
    summary:
      "Live and on-demand fitness classes, supported by a rebuilt platform and updated mobile app.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Sprint Zero + development",
      },
      {
        label: "PLATFORMS",
        value: "Web + mobile",
      },
      {
        label: "CONTEXT",
        value: "Streaming fitness",
      },
    ],
    brief: "Give the product room to grow.",
    briefBody:
      "obé’s founders wanted people to work out whenever and wherever they chose. As the offering grew, they needed a platform that could support live classes, on-demand workouts, and the experience members expected.",
    rows: [
      {
        heading: "Rebuild the foundation.",
        body: "After a Sprint Zero assessment, we rewrote the front and back end and updated the mobile app. The work gave obé a more customizable foundation for its live and on-demand fitness offering.",
        image: "/images/cases/ae84b.png",
      },
      {
        heading: "Support the way members watch.",
        body: "We replaced the previous streaming setup with a combination of Vimeo and JW Player. The updated platform brought the workout experience across web and mobile, supporting both scheduled classes and workouts on demand.",
        image: "/images/cases/533f7.png",
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "A platform for more ways to move.",
      body: "The rebuilt product supported obé’s growing class offering and the different ways members wanted to fit workouts into their day.",
      quote: false,
    },
  },
  "bold-new-york": {
    headline: "Less juggling.\nMore time\nfor clients.",
    summary: "Custom listing, deal, and relationship tools for a New York real estate brokerage.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Development + training",
      },
      {
        label: "PLATFORMS",
        value: "Web",
      },
      {
        label: "CONTEXT",
        value: "Real estate",
      },
    ],
    brief: "Connect the work behind every deal.",
    briefBody:
      "Bold’s brokers handled listings, applications, negotiations, and client support in a competitive market. The team needed software that connected those operations and fit how its agents worked.",
    rows: [
      {
        heading: "Publish once. Keep the work moving.",
        body: "We built tools to create and publish listings to Bold’s website, StreetEasy, and the MLS. An automated deal-flow platform helped connect the steps behind the transaction.",
        image: "/images/cases/6d8ff.png",
      },
      {
        heading: "Give agents the tools to use it.",
        body: "The engagement included agent dashboards, reporting, and a custom CRM. Documentation, training, and feedback sessions helped the team adopt the new platform in its day-to-day work.",
        image: "/images/cases/31509.png",
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "A platform shaped around the brokerage.",
      body: "Bold replaced older off-the-shelf tools with a custom system for listings, deals, and client relationships, creating a foundation it could continue to develop.",
      quote: false,
    },
  },
  "the-forem": {
    headline: "From a pitch deck\nto a product\npeople could use.",
    summary:
      "A learning and development platform, shaped from the first prototype through the MVP and beyond.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Product + design + development",
      },
      {
        label: "PLATFORMS",
        value: "Web",
      },
      {
        label: "CONTEXT",
        value: "Learning and development",
      },
    ],
    brief: "Make the idea tangible.",
    briefBody:
      "Founder Alli Young wanted a platform where leaders could connect and mentor others. We worked with The Forem from its pitch deck through an MVP, helping the team turn an idea into something it could show, test, and build on.",
    rows: [
      {
        heading: "Start with the people and the flows.",
        body: "Sprint Zero defined the users, workflows, and core product concepts. An interactive prototype helped support fundraising and product validation before the team moved into development.",
        image: "/images/cases/75689.png",
      },
      {
        heading: "Stay involved as the team grows.",
        body: "After the MVP, our work continued across development, design, product management, and technical advice. We also helped with hiring support for The Forem’s founding engineer.",
        image: "/images/cases/3c6dd.png",
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "An MVP with a path forward.",
      body: "The prototype and working product supported The Forem’s fundraising and early development, followed by continued support as the company built its own team.",
      quote: false,
    },
  },
  "makerbot": {
    headline: "A whole fleet.\nOne place to\nrun it.",
    summary: "Software for coordinating 3D printing across MakerBot Innovation Centers.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Software development + handoff",
      },
      {
        label: "PLATFORMS",
        value: "Printer fleet software",
      },
      {
        label: "CONTEXT",
        value: "Education and fabrication",
      },
    ],
    brief: "Think beyond a single printer.",
    briefBody:
      "MakerBot Innovation Centers needed software that could coordinate a fleet of 3D printers. We worked with MakerBot to support printing hundreds of items at the same time across those machines.",
    rows: [
      {
        heading: "Build for the fleet.",
        body: "We developed the software powering Innovation Centers, where many printers and print jobs needed to work together. The platform supported the practical demands of printing at that scale.",
        image: "/images/cases/d46c7.png",
      },
      {
        heading: "Set the growing team up to own it.",
        body: "As MakerBot’s team grew, we added functionality and supported a clean handoff. The work included complex university login integrations and continued guidance as questions came up.",
        image: "/images/cases/07c32.png",
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "Built to run. Ready to hand over.",
      body: "MakerBot received fleet software and the support its growing team needed to take the platform forward.",
      quote: false,
    },
  },
  "dapper-labs": {
    headline: "A better starting\npoint for Flow\ndevelopers.",
    summary:
      "A reference marketplace and developer tools that make it easier to build and test on Flow.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Sprint Zero + design + development",
      },
      {
        label: "PLATFORMS",
        value: "Web / Flow",
      },
      {
        label: "CONTEXT",
        value: "Developer tools",
      },
    ],
    brief: "Help developers get building.",
    briefBody:
      "Dapper Labs needed tools that made developing on Flow easier to understand and put into practice. We worked together on a testnet faucet, a reference marketplace, and a development wallet, starting with the workflows developers needed every day.",
    rows: [
      {
        heading: "Show how it works.",
        body: "KittyItems gives developers a working example of an NFT marketplace on Flow. We designed the marketplace experience and collaborated with Dapper’s blockchain developers to deploy the reference application to the Flow testnet.",
        image: "/images/cases/a8c9c.png",
      },
      {
        heading: "Make testing easier.",
        body: "The testnet faucet supplies the FLOW developers need to test applications. The development wallet brings the details needed to debug code into an approachable interface. We worked with Dapper to integrate the wallet into its developer tool suite.",
        image: "/images/cases/4b6f4.png",
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "Tools developers can build on.",
      body: "A working marketplace example, a testnet faucet, and a development wallet give Flow developers practical tools for building and testing their own applications.",
      quote: false,
    },
  },
  "skillschool": {
    headline: "Share what\nyou know.\nMake it a class.",
    summary:
      "A learning platform built around creating lessons, taking classes, and reviewing assignments.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Sprint Zero + product design",
      },
      {
        label: "PLATFORMS",
        value: "Web",
      },
      {
        label: "CONTEXT",
        value: "Learning and education",
      },
    ],
    brief: "Make teaching easier to organize.",
    briefBody:
      "SkillSchool wanted to make curriculum development and student assessment easier for teachers and people with expertise to share. The product needed to work for both the person building a class and the person taking it.",
    rows: [
      {
        heading: "Design both sides of the classroom.",
        body: "Starting with Sprint Zero, we defined and designed a custom learning management system. The work covered class creation and grading for instructors, alongside the experience of taking a class as a student.",
        image: "/images/cases/9cdbc.png",
      },
      {
        heading: "Make good classes reusable.",
        body: "A lesson editor and custom assignment module supported the teaching workflow. Classes could also be duplicated and taught by another organization, making it easier to share the work that went into a curriculum.",
        image: "/images/cases/9578c.png",
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "A class can reach beyond one classroom.",
      body: "SkillSchool’s model combines lesson creation, assignments, and reusable classes in a product designed for instructors and learners.",
      quote: false,
    },
  },
  "the-information": {
    headline: "A news app.\nFor people who\nknow tech.",
    summary: "From concept to iOS and Android, in time for The Information’s annual conference.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Design + development",
      },
      {
        label: "PLATFORMS",
        value: "iOS + Android",
      },
      {
        label: "CONTEXT",
        value: "Launched in 2019",
      },
    ],
    brief: "A new way to reach readers.",
    briefBody:
      "The Information wanted to turn its reporting into a new mobile subscription offering, Tech Top 10. The launch had a fixed date: its annual conference. We had ten weeks for design, development, testing, and both app store submissions.",
    rows: [
      {
        heading: "From concept to a working app.",
        body: "Within the first month, the team had a name, designs, and an app in internal testing. We worked closely with The Information to turn the reading experience into a product ready for both mobile platforms.",
        image: "/images/cases/71190.png",
      },
      {
        heading: "Ready for the conference.",
        body: "We navigated the app store submission process and released on iOS and Android in time for the conference. The work continued after launch, expanding the app’s content and adding ways for subscribers to connect around shared interests.",
        image: "/images/cases/46cc5.png",
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "A launch became a partnership.",
      body: "The first app led to ongoing collaboration across The Information’s web and mobile products, including more content, notifications, and community features.",
      quote: false,
    },
  },
  "pearland": {
    headline: "Renew the policy.\nSkip the\noffice visit.",
    summary:
      "Online policy renewals and payments for New York’s taxi, livery, and rideshare drivers.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Product development + ongoing support",
      },
      {
        label: "PLATFORMS",
        value: "Web",
      },
      {
        label: "CONTEXT",
        value: "Insurance operations",
      },
    ],
    brief: "Bring the brokerage online.",
    briefBody:
      "Pearland’s customers previously had to visit an office or call to handle policy tasks. We built an online platform for renewals, payments, and policy changes, connected to the systems its staff used behind the scenes.",
    rows: [
      {
        heading: "Put routine tasks in drivers’ hands.",
        body: "Drivers can claim policies, get quotes, renew, pay in installments, enroll in autopay, and request endorsements online. Stripe payments stay connected to Pearland’s policy system of record.",
        steps: [
          "Claim or quote a policy",
          "Renew and choose payments",
          "Policy system stays in sync",
        ],
      },
      {
        heading: "Keep the staff workflow connected.",
        body: "Staff queues organize renewals, endorsements, and quotes. After the 2017 launch, the work continued through a policy-system migration, payment updates, and automated customer notices.",
        steps: ["Renewal, quote, or endorsement", "Staff review queue", "Customer notices"],
      },
    ],
    outcome: {
      label: "WHAT CHANGED",
      heading: "A platform with years of work behind it.",
      body: "The engagement grew from an online launch into ongoing support and development, keeping customer self-service connected to the brokerage’s operations.",
      quote: false,
    },
  },
  "hadley": {
    headline: "College savings.\nWith everyone\npitching in.",
    summary:
      "A mobile app that helps family and friends contribute to a child’s education savings.",
    facts: [
      {
        label: "OUR ROLE",
        value: "Research + design + development",
      },
      {
        label: "PLATFORMS",
        value: "iOS + Android",
      },
      {
        label: "CONTEXT",
        value: "Financial services",
      },
    ],
    brief: "Make contributing feel simple.",
    briefBody:
      "Opening an education savings plan is one thing. Helping family and friends contribute is another. Hadley wanted contributing to a 529 plan to feel as straightforward as sending a payment. The systems behind that experience were anything but straightforward.",
    rows: [
      {
        heading: "Start with the flow.",
        body: "We worked with founder Yosh Miller from the first prototype, mapping how contributions should work and researching the systems behind them. That early work helped the team connect a simple user experience to a complicated financial process.",
        image: "/images/cases/fe94a.png",
      },
      {
        heading: "Connect the pieces.",
        body: "Working with the Hadley team and tools including Plaid, Stripe, and Skyflow, we designed and built the payment flow between bank accounts and education savings plans. The app keeps the steps clear for the person making a contribution.",
        image: "/images/cases/3ae48.png",
      },
    ],
    outcome: {
      label: "IN THEIR WORDS",
      heading: "“Consultative doesn’t even scratch the surface of how collaborative this team is.”",
      body: "Yosh Miller · Founder, Hadley",
      quote: true,
    },
  },
}
